import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function Login() {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'verify'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  
  const { currentUser, signInWithPhone, verifyCode } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser && currentUser.uid) {
      const redirectUser = async () => {
        try {
          // Redirect all users (including admins) to home page
          navigate('/home');
        } catch (error) {
          console.error('Error during redirect:', error);
          // Fallback to home if there's an error
          navigate('/home');
        }
      };
      
      redirectUser();
    }
  }, [currentUser, navigate]);

  const handleVerificationSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyCode(verificationCode);
      // Store name in localStorage for use in survey
      localStorage.setItem('userName', name);
      
      // Redirect all users (including admins) to home page
      navigate('/home');
    } catch (error) {
      console.error('Verification error:', error);
      if (error.message.includes('No confirmation result')) {
        setError('Session expired. Please request a new verification code.');
        setStep('phone');
      } else if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else if (error.code === 'auth/code-expired') {
        setError('Verification code expired. Please request a new one.');
        setStep('phone');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [verificationCode, verifyCode, name, navigate]);

  // Handle verification code input changes
  const handleCodeChange = (e) => {
    const value = e.target.value;
    // Only allow numeric input
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(numericValue);
    setIsCodeComplete(numericValue.length === 6 && /^\d{6}$/.test(numericValue));
  };

  // Auto-paste functionality for mobile
  useEffect(() => {
    if (step === 'verify') {
      const codeInput = document.getElementById('code');
      if (codeInput) {
        codeInput.focus();
      }

      // Listen for paste events
      const handlePaste = (e) => {
        const pastedText = e.clipboardData.getData('text');
        // Check if pasted text looks like a verification code (6 digits)
        if (/^\d{6}$/.test(pastedText)) {
          setVerificationCode(pastedText);
          setIsCodeComplete(true);
          // Auto-submit after a short delay for pasted content
          setTimeout(() => {
            const syntheticEvent = { preventDefault: () => {} };
            handleVerificationSubmit(syntheticEvent);
          }, 100);
        }
      };

      if (codeInput) {
        codeInput.addEventListener('paste', handlePaste);
        
        return () => {
          codeInput.removeEventListener('paste', handlePaste);
        };
      }
    }
  }, [step, handleVerificationSubmit]);

  const formatPhoneNumber = (phone) => {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Add +1 if it's a 10-digit US number
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    
    // Add + if it doesn't start with +
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    
    // Return as is if it already has country code
    return phone.startsWith('+') ? phone : `+${cleaned}`;
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      await signInWithPhone(formattedPhone);
      setStep('verify');
    } catch (error) {
      setError('Failed to send verification code. Please try again.');
      console.error('Phone auth error:', error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">🏔️ Whistler Trip</h1>
          <p className="card-subtitle">
            {step === 'phone' 
              ? 'Help us plan our group trip to Whistler, BC!'
              : 'Enter the verification code sent to your phone'
            }
          </p>
        </div>

        {error && <div className="error">{error}</div>}

        {step === 'phone' ? (
          <form onSubmit={handlePhoneSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                What's your name?
              </label>
              <input
                type="text"
                id="name"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="phone">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                className="form-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
                required
              />
              <small style={{ color: '#718096', fontSize: '0.9rem', marginTop: '5px', display: 'block' }}>
                We'll send you a verification code via SMS
              </small>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerificationSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="code">
                Verification Code
              </label>
              <input
                type="text"
                id="code"
                className="form-input"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="Enter 6-digit code"
                maxLength="6"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                autoFocus
                required
                style={{
                  fontSize: '1.2rem',
                  textAlign: 'center',
                  letterSpacing: '0.2em',
                  fontFamily: 'monospace'
                }}
              />
              <small style={{ color: '#718096', fontSize: '0.9rem', marginTop: '5px', display: 'block' }}>
                Check your messages for the verification code. Paste to auto-submit, or type and verify before submitting.
              </small>
              {isCodeComplete && (
                <div style={{ 
                  background: '#c6f6d5', 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  marginTop: '10px',
                  border: '1px solid #9ae6b4',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, color: '#2f855a', fontSize: '0.8rem', fontWeight: '600' }}>
                    ✅ Code complete! Click "Verify & Continue" to proceed.
                  </p>
                </div>
              )}
            </div>

            <div className="navigation">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setStep('phone')}
                disabled={loading}
              >
                Back
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || !isCodeComplete}
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
